import {
  Card,
  Divider,
  Grid,
  Table,
  TableBody,
  TableHead,
  TableRow,
  Typography,
  Pagination,
  Snackbar,
  Alert as MuiAlert,
  TableContainer,
  CardContent,
  Box,
  CircularProgress,
  Tooltip,
  IconButton,
  TextField,
  Button
} from '@mui/material'
import MainCard, {
  SnackBarHideDuration,
  SnackBarTransition,
  StyledTableCell,
  StyledTableRow,
  ShowNoRecordsFound,
  ErrorScreen,
  SkeletonCard,
  LoadingDialog
} from '../../utils/ui_utils'
import moment from 'moment'
import { useEffect, useState, forwardRef } from 'react'
import useReportRefresh from '../../hooks/useReportRefresh'
import { Helmet } from 'react-helmet-async'
import { useNavigate } from 'react-router-dom'
import { businessCheck, isEmpty } from '../../utils/utils'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import { BarChart } from '../../components/Chart'
import { OutletAndDatePickerUI } from '../../utils/ui_components/outletAndDatePickerUi.js'
import ListTypeShimmer from '../../utils/ui_components/shimmers/listTypeShimmer'
import {
  useDownloadReports,
  useFetchBillwiseReport,
  useFetchOutletSummary,
  useFetchBillDetails
} from '../../modules/reports/reportsHooks'
import { useFetchOutlet } from '../../modules/outlet/outletHooks'
import { useDispatch } from 'react-redux'
import { generateXLSXCSV } from '../../utils/tsUtils/utils.js'
import { downloadReportsSlice } from '../../modules/reports/reportsSlice.js'
import BillDetailsDialog from './components/BillDetailsDialog'
import { getUserType } from '../../components/tsComponents/AuthGuard'
import ReportRefreshControls from '../../utils/ui_components/ReportRefreshControls'

const Alert = forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />
})

const BillwiseReport = () => {
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(15)
  const [pagecount, setPageCount] = useState(1)
  const [selectedOutlet, setSelectedOutlet] = useState('')
  const [snackbar, setSnackBar] = useState(false)
  const [PopUpMessage, setPopUpMessage] = useState(null)
  const [severity, setSeverity] = useState(null)

  // New states for all data, graph, and table
  const [allBillData, setAllBillData] = useState([])
  const [graphData, setGraphData] = useState({
    labels: [moment().format('MMM-DD')],
    datasets: [
      {
        label: 'Total Sales',
        data: [0],
        backgroundColor: ['#ffbb11']
      }
    ]
  })
  const [terminalGraphsData, setTerminalGraphsData] = useState([]) // Array of graph data for each terminal
  const [tableData, setTableData] = useState([])
  const [fromDate, setFromDate] = useState(moment().startOf('day'))
  const [toDate, setToDate] = useState(moment().startOf('day'))
  const [showError, setShowError] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [showToDateError, setShowToDateError] = useState(false)
  const [toDateErrorMessage, setToDateErrorMessage] = useState(
    'To Date is earlier than From Date'
  )
  const [reportVisibility, setReportVisibility] = useState(false)
  const [goToPageInput, setGoToPageInput] = useState('')
  const { outletFetch, outletFetchData, outletFetchError, outletFetchLoading } =
    useFetchOutlet()
  const {
    outletSummaryData,
    outletSummaryError,
    outletSummaryFetch,
    outletSummaryLoading
  } = useFetchOutletSummary()
  const {
    billwiseReportData,
    billwiseReportError,
    billwiseReportFetch,
    billwiseReportLoading
  } = useFetchBillwiseReport()

  const { downloadReportsLoading, downloadReportsFetch } = useDownloadReports()

  const {
    billDetailsData,
    billDetailsError,
    billDetailsFetch,
    billDetailsLoading
  } = useFetchBillDetails()

  const [billDetailsDialogOpen, setBillDetailsDialogOpen] = useState(false)

  // Determine user type via AuthGuard
  const userType = getUserType()
  const isOwner = userType === 'Owner'

  // Build outlets with an explicit "All Outlets" (id '0') and remove any duplicates
  const baseOutlets = Array.isArray(outletFetchData?.data)
    ? outletFetchData.data.filter(
        (o) => String(o?._id) !== '0' && o?.name !== 'All Outlets'
      )
    : []
  const outletsWithAll = isOwner
    ? [{ _id: '0', name: 'All Outlets' }, ...baseOutlets]
    : baseOutlets

  // Helper: only '0' means All Outlets
  const isAllOutletsSelected = isOwner && selectedOutlet === '0'

  const dispatch = useDispatch()
  const handleSnackbarClose = () => {
    setSnackBar(false)
  }

  const validateAndGetReport = (page) => {
    let tempFromDate = moment(fromDate)
    let tempToDate = moment(toDate)
    if (tempFromDate.isAfter(tempToDate)) {
      return
    }
    // Prevent all-outlets fetch for non-owners until an outlet is selected
    if (!isOwner && isEmpty(selectedOutlet)) {
      return
    }
    // Always fetch reports and summary
    fetchRecord(page)
    fetchRecordSummary()
  }

  const fetchRecord = (page) => {
    let payload = {
      from_date: moment(fromDate).format('YYYY-MM-DD'),
      to_date: moment(toDate).add(1, 'days').format('YYYY-MM-DD'),
      pageNumber: 1,
      limit: 1000 // Use a higher limit to get more data
    }

    // Only include outlet_id if a specific outlet is selected
    if (!isAllOutletsSelected) {
      payload.outlet_id = selectedOutlet
    }

    billwiseReportFetch(payload)
  }

  const fetchRecordSummary = () => {
    let payload = {
      from_date: moment(fromDate).format('YYYY-MM-DD'),
      to_date: moment(toDate).add(1, 'days').format('YYYY-MM-DD')
    }

    // Only include outlet_id if a specific outlet is selected
    if (!isAllOutletsSelected) {
      payload.outlet_id = selectedOutlet
    }

    outletSummaryFetch(payload)
  }

  const fetchBillwiseReportOnError = () => {
    let payload = {
      from_date: moment(fromDate).format('YYYY-MM-DD'),
      to_date: moment(toDate).add(1, 'days').format('YYYY-MM-DD'),
      pageNumber: page,
      limit: limit
    }

    // Only include outlet_id if a specific outlet is selected
    if (!isAllOutletsSelected) {
      payload.outlet_id = selectedOutlet
    }

    billwiseReportFetch(payload)
  }

  const fetchOutletOnError = () => {
    let payload = {
      pageNumber: 1,
      limit: 1000
    }
    outletFetch(payload)
  }

  const toastMessage = (message, severity) => {
    setPopUpMessage(message)
    setSeverity(severity)
    setSnackBar(true)
  }

  const handlePageChange = async (event, value) => {
    setPage(value)
    // Client-side pagination: update tableData from allBillData
    const startIdx = (value - 1) * limit
    const endIdx = startIdx + limit
    setTableData(allBillData.slice(startIdx, endIdx))
  }

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    animation: false, // Disable animations
    // Remove categoryPercentage and barPercentage as they're now handled in Chart.js
    scales: {
      x: {
        title: {
          display: true,
          text: 'Bill Numbers',
          align: 'center',
          font: {
            weight: 'bold',
            size: 14
          }
        },
        ticks: {
          font: {
            size: 10 // Slightly larger font for better readability
          },
          autoSkip: false, // Don't skip labels as scroll handles overflow
          maxTicksLimit: undefined // Remove limit to show all labels
        },
        grid: {
          display: true,
          color: 'rgba(0,0,0,0.1)'
        }
      },
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: 'Amount (₹)',
          font: {
            weight: 'bold',
            size: 14
          }
        },
        ticks: {
          font: {
            size: 11
          }
        },
        grid: {
          display: true,
          color: 'rgba(0,0,0,0.1)'
        }
      }
    },
    elements: {
      bar: {
        borderWidth: 1,
        borderColor: 'rgba(0,0,0,0.2)',
        borderRadius: 4 // Rounded corners for better appearance
      }
    },
    plugins: {
      title: {
        display: true,
        text: 'Sales Trend',
        padding: {
          top: 10,
          bottom: 20 // Reduced bottom padding
        },
        font: {
          size: 16,
          weight: 'bold'
        }
      },
      legend: {
        display: true,
        position: 'bottom'
      },
      datalabels: {
        display: true,
        color: 'black',
        formatter: Math.round,
        anchor: 'end',
        offset: -20,
        align: 'start',
        font: {
          size: 10,
          weight: 'bold'
        }
      }
    }
  }

  const pageCount = (data1) => {
    let count = Math.ceil(data1 / limit)
    setPageCount(count)
  }

  const history = useNavigate()

  useEffect(() => {
    dispatch(downloadReportsSlice.actions.reset())
    businessCheck(history)
    let payload = {
      pageNumber: 1,
      limit: 1000
    }
    outletFetch(payload)
  }, [])

  useEffect(() => {
    // Default selection based on role after outlets load
    if (isOwner) {
      setSelectedOutlet('0')
    } else if (
      Array.isArray(outletFetchData?.data) &&
      outletFetchData.data.length > 0
    ) {
      setSelectedOutlet(String(outletFetchData.data[0]._id))
    } else {
      setSelectedOutlet('')
    }
  }, [outletFetchData, isOwner])

  useEffect(() => {
    if (billwiseReportData.data?.length > 0) {
      // Store all data in state
      setAllBillData(billwiseReportData.data)

      // Group data by terminal
      const terminalGroups = {}
      billwiseReportData.data.forEach((item) => {
        const terminal = item['Terminal'] || 'Unknown'
        if (!terminalGroups[terminal]) {
          terminalGroups[terminal] = []
        }
        terminalGroups[terminal].push(item)
      })

      const terminals = Object.keys(terminalGroups)
      const colorPalette = [
        '#e0218a',
        '#ffbb11',
        '#ff5e57',
        '#00d25b',
        '#9c27b0',
        '#ff9800',
        '#4caf50',
        '#2196f3'
      ]

      // Set graph data based on outlet selection
      if (isAllOutletsSelected) {
        // If All Outlets selected, show aggregated view (one bar per terminal)
        const terminalData = {}
        billwiseReportData.data.forEach((item) => {
          const terminal = item['Terminal'] || 'Unknown'
          if (!terminalData[terminal]) {
            terminalData[terminal] = 0
          }
          terminalData[terminal] += item['Total'] || 0
        })

        setGraphData({
          labels: Object.keys(terminalData),
          datasets: [
            {
              label: 'Total Sales by Terminal',
              data: Object.values(terminalData),
              backgroundColor: colorPalette,
              borderColor: colorPalette,
              borderWidth: 2
            }
          ]
        })
        setTerminalGraphsData([]) // Clear terminal graphs data
      } else {
        // If specific outlet is selected and multiple terminals, create separate graphs
        if (terminals.length > 1) {
          const terminalGraphs = terminals.map((terminal, index) => ({
            terminalName: terminal,
            data: {
              labels: terminalGroups[terminal].map((data) => data['Bill_Num']),
              datasets: [
                {
                  label: `Sales - ${terminal}`,
                  data: terminalGroups[terminal].map((data) => data['Total']),
                  backgroundColor: colorPalette[index % colorPalette.length],
                  borderColor: colorPalette[index % colorPalette.length],
                  borderWidth: 2
                }
              ]
            }
          }))
          setTerminalGraphsData(terminalGraphs)
          setGraphData(null) // Clear single graph data
        } else {
          // Single terminal - show individual bill data
          setGraphData({
            labels: billwiseReportData.data.map((data) => data['Bill_Num']),
            datasets: [
              {
                label: 'Total Sales',
                data: billwiseReportData.data.map((data) => data['Total']),
                backgroundColor: ['#e0218a', '#ffbb11', '#ff5e57', '#00d25b'],
                borderColor: ['#c91e82', '#e6a300', '#ff4757', '#00b894'],
                borderWidth: 2
              }
            ]
          })
          setTerminalGraphsData([])
        }
      }

      // Set table data for current page
      const startIdx = (page - 1) * limit
      const endIdx = startIdx + limit
      setTableData(billwiseReportData.data.slice(startIdx, endIdx))

      pageCount(billwiseReportData.data.length)
      setReportVisibility(true)
    } else {
      setAllBillData([])
      setGraphData([])
      setTerminalGraphsData([])
      setTableData([])
      setReportVisibility(false)
    }
  }, [billwiseReportData, page, limit, selectedOutlet])

  const handleOutletChange = (event) => {
    setSelectedOutlet(event.target.value)
  }

  const handleGoToPageChange = (event) => {
    const value = event.target.value
    if (value === '' || /^\d+$/.test(value)) {
      setGoToPageInput(value)
    }
  }

  const handleGoToPageSubmit = () => {
    const pageNumber = parseInt(goToPageInput, 10)
    if (pageNumber >= 1 && pageNumber <= pagecount) {
      handlePageChange(null, pageNumber)
      setGoToPageInput('')
    }
  }

  const handleGoToPageKeyPress = (event) => {
    if (event.key === 'Enter') {
      handleGoToPageSubmit()
    }
  }

  const refreshFunction = () => validateAndGetReport(page)
  const { autoRefresh, handleToggleAutoRefresh, handleManualRefresh } =
    useReportRefresh(refreshFunction, [page, selectedOutlet, fromDate, toDate])

  useEffect(() => {
    setPage(1)
    validateAndGetReport(1)
  }, [selectedOutlet, fromDate, toDate])

  const exportToCsv = async () => {
    try {
      let payload = {
        from_date: moment(fromDate).format('YYYY-MM-DD'),
        to_date: moment(toDate).add(1, 'days').format('YYYY-MM-DD'),
        pageNumber: 1,
        limit: 100,
        total: billwiseReportData?.total || 15,
        type: 'billwise'
      }

      // Only include outlet_id if a specific outlet is selected
      if (!isAllOutletsSelected) {
        payload.outlet_id = selectedOutlet
      }

      const reports = await downloadReportsFetch(payload)

      let data1 = [...reports.payload]

      // Add summary row similar to inventory report pattern (only if outlet summary data exists)
      if (outletSummaryData && outletSummaryData.length > 0) {
        data1.push(
          {}, // to add empty row
          {
            Bill_Date: 'Total Amount',
            Bill_Num: outletSummaryData[0]?.Total_Bills || 0,
            Total_GST: outletSummaryData[0]?.Total_GST?.toFixed(2) || '0.00',
            Total_Cess: outletSummaryData[0]?.Total_Cess?.toFixed(2) || '0.00',
            Total_Disc: outletSummaryData[0]?.Total_Disc?.toFixed(2) || '0.00',
            Total_Qty: outletSummaryData[0]?.Total_Qty?.toFixed(3) || '0.000',
            Total: outletSummaryData[0]?.Total_Sales?.toFixed(2) || '0.00'
          }
        )
      }

      const options = {
        keys: [
          { field: 'Bill_Date', title: 'Date' },
          { field: 'Bill_Num', title: 'Bill Number' },
          { field: 'Terminal', title: 'Terminal' },
          { field: 'Total_Qty', title: 'Quantity' },
          { field: 'Total_GST', title: 'GST' },
          { field: 'Total_Cess', title: 'Cess' },
          { field: 'Total_Disc', title: 'Discount' },
          { field: 'PaymentMode', title: 'Payment Mode' },
          { field: 'Total', title: 'Total' }
        ]
      }

      let outletName = 'All_Outlets'
      if (!isAllOutletsSelected) {
        const selectedOutletData = outletFetchData.data.find(
          (outlet) => String(outlet._id) === String(selectedOutlet)
        )
        outletName = selectedOutletData?.name || 'Unknown_Outlet'
      }

      let filename = `Billwise_Report_${outletName}`
      filename += `_${payload.from_date}_to_${payload.to_date}`

      const csv = await generateXLSXCSV(data1, options.keys, filename)

      toastMessage('CSV Downloaded Successfully')
    } catch (error) {
      console.error('Error while converting JSON to CSV', error)
      toastMessage('Error While Downloading csv', 'error')
    }
  }

  const handleBillRowClick = async (billId) => {
    try {
      setBillDetailsDialogOpen(true)
      await billDetailsFetch(billId)
    } catch (error) {
      console.error('Error fetching bill details:', error)
      toastMessage('Error fetching bill details', 'error')
    }
  }

  const handleBillDetailsDialogClose = () => {
    setBillDetailsDialogOpen(false)
  }

  return (
    <Box
      sx={{
        width: '100%',
        minHeight: '100vh',
        p: { xs: 0.5, sm: 1, md: 2 },
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        maxWidth: 'calc(100vw - 300px)', // Account for sidebar width
        '@media (max-width: 960px)': {
          maxWidth: '100vw' // Full width on mobile when sidebar is collapsed
        }
      }}
    >
      <Helmet title="Billwise Report" />

      <Grid
        container
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: { xs: 1, sm: 2 }
        }}
      >
        <Grid item>
          <Typography
            variant="h4"
            gutterBottom
            display="inline"
            sx={{
              fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' }
            }}
          >
            Billwise Report
          </Typography>
        </Grid>
        <Grid item>
          <ReportRefreshControls
            autoRefresh={autoRefresh}
            onToggleAutoRefresh={handleToggleAutoRefresh}
            onManualRefresh={handleManualRefresh}
            isLoading={billwiseReportLoading}
          />
        </Grid>
      </Grid>
      <Divider sx={{ marginTop: 2 }} />
      {outletFetchError ? (
        <ErrorScreen
          onClick={() => {
            fetchOutletOnError()
          }}
        />
      ) : (
        <div>
          <OutletAndDatePickerUI
            outlets={outletsWithAll}
            onOutletChange={handleOutletChange}
            selectedOutlet={selectedOutlet}
            fromDate={fromDate}
            toDate={toDate}
            onFromDateChange={(newValue) => {
              newValue.setHours(0, 0, 0)
              setFromDate(newValue)
              if (moment(toDate).isBefore(newValue)) {
                setToDateErrorMessage('To Date is earler than From Date')
                setShowToDateError(true)
              } else {
                setToDateErrorMessage('')
                setShowToDateError(false)
              }
            }}
            onFromDateError={(reason, value) => {
              if (reason) {
                setErrorMessage(reason)
                setShowError(true)
              } else {
                setErrorMessage('')
                setShowError(false)
              }
            }}
            onToDateChange={(newValue) => {
              newValue.setHours(0, 0, 0)
              setToDate(newValue)
              if (moment(fromDate).isAfter(newValue)) {
                setToDateErrorMessage('To Date is earler than From Dates')
                setShowToDateError(true)
              } else {
                setToDateErrorMessage('')
                setShowToDateError(false)
              }
            }}
            onToDateError={(reason, value) => {
              if (reason) {
                setToDateErrorMessage(reason)
                setShowToDateError(true)
              } else {
                setToDateErrorMessage('')
                setShowToDateError(false)
              }
            }}
            showFromDateError={showError}
            showToDateError={showToDateError}
            fromDateErrorMessage={errorMessage}
            toDateErrorMessage={toDateErrorMessage}
            exportComponent={
              billwiseReportData.data &&
              billwiseReportData.data.length > 0 && (
                <Tooltip title="Export to CSV" placement="top" arrow>
                  <IconButton
                    variant="contained"
                    onClick={exportToCsv}
                    disabled={downloadReportsLoading}
                    size="large"
                  >
                    {downloadReportsLoading && <CircularProgress size={20} />}
                    {!downloadReportsLoading ? (
                      <>
                        {' '}
                        <CloudUploadIcon fontSize="large" />
                      </>
                    ) : (
                      <> </>
                    )}
                  </IconButton>
                </Tooltip>
              )
            }
          />

          {billwiseReportError ? (
            <ErrorScreen
              onClick={() => {
                fetchBillwiseReportOnError()
              }}
            />
          ) : (
            <div>
              {!downloadReportsLoading ? (
                <>
                  {(billwiseReportLoading == true ||
                    billwiseReportLoading == null ||
                    outletFetchLoading) && (
                    <Grid
                      container
                      spacing={10}
                      direction={'column'}
                      sx={{ paddingTop: 5 }}
                    >
                      <Grid item>
                        <Grid
                          container
                          rowSpacing={4.5}
                          columnSpacing={2}
                          sx={{ mt: 1 }}
                        >
                          <Grid item xs={12}>
                            <MainCard content={false} sx={{ mt: 1.5 }}>
                              <Box sx={{ pt: 1, pr: 2, pl: 2, pb: 1 }}>
                                <ListTypeShimmer />
                              </Box>
                            </MainCard>
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>
                  )}

                  {!billwiseReportError &&
                    !outletFetchLoading &&
                    !outletFetchError &&
                    !billwiseReportLoading &&
                    reportVisibility && (
                      <Grid
                        container
                        spacing={{ xs: 2, sm: 4, md: 6, lg: 10 }}
                        direction={'column'}
                        sx={{
                          paddingTop: { xs: 2, sm: 3, md: 5 },
                          width: '100%',
                          maxWidth: '100%',
                          overflow: 'hidden'
                        }}
                      >
                        <Grid item>
                          <Grid
                            container
                            rowSpacing={4.5}
                            columnSpacing={2}
                            sx={{ mt: 1 }}
                          >
                            {/* Render multiple graphs for multiple terminals */}
                            {terminalGraphsData.length > 0
                              ? terminalGraphsData.map(
                                  (terminalGraph, index) => (
                                    <Grid item xs={12} key={index}>
                                      <Typography variant="h5" gutterBottom>
                                        Sales Trend -{' '}
                                        {terminalGraph.terminalName}
                                      </Typography>
                                      <MainCard
                                        content={false}
                                        sx={{
                                          mt: 1.5,
                                          overflow: 'hidden',
                                          width: '100%',
                                          maxWidth: 'calc(100vw - 375px)',
                                          '@media (max-width: 960px)': {
                                            maxWidth: '100vw'
                                          }
                                        }}
                                      >
                                        <Box
                                          sx={{
                                            pt: 1,
                                            pr: 1,
                                            pl: 1,
                                            pb: 1,
                                            width: '100%',
                                            maxWidth: '100%',
                                            overflow: 'hidden',
                                            height: {
                                              xs: '300px',
                                              sm: '350px',
                                              md: '400px'
                                            }
                                          }}
                                        >
                                          <BarChart
                                            chartData={terminalGraph.data}
                                            options={{
                                              ...chartOptions,
                                              plugins: {
                                                ...chartOptions.plugins,
                                                title: {
                                                  display: true,
                                                  text: `Sales Trend - ${terminalGraph.terminalName}`,
                                                  padding: {
                                                    top: 10,
                                                    bottom: 20
                                                  },
                                                  font: {
                                                    size: 16,
                                                    weight: 'bold'
                                                  }
                                                }
                                              }
                                            }}
                                          />
                                        </Box>
                                      </MainCard>
                                    </Grid>
                                  )
                                )
                              : /* Render single graph when only one terminal or aggregated view */
                                graphData && (
                                  <Grid item xs={12}>
                                    <Typography variant="h5" gutterBottom>
                                      Sales Trend
                                    </Typography>
                                    <MainCard
                                      content={false}
                                      sx={{
                                        mt: 1.5,
                                        overflow: 'hidden',
                                        width: '100%',
                                        maxWidth: 'calc(100vw - 375px)',
                                        '@media (max-width: 960px)': {
                                          maxWidth: '100vw'
                                        }
                                      }}
                                    >
                                      <Box
                                        sx={{
                                          pt: 1,
                                          pr: 1,
                                          pl: 1,
                                          pb: 1,
                                          width: '100%',
                                          maxWidth: '100%',
                                          overflow: 'hidden',
                                          height: {
                                            xs: '300px',
                                            sm: '350px',
                                            md: '400px'
                                          }
                                        }}
                                      >
                                        <BarChart
                                          chartData={graphData}
                                          options={chartOptions}
                                        />
                                      </Box>
                                    </MainCard>
                                  </Grid>
                                )}
                          </Grid>
                        </Grid>

                        <Grid item>
                          <Grid container spacing={2}>
                            <Grid item xs={12}>
                              <MainCard
                                content={false}
                                sx={{
                                  width: '100%',
                                  maxWidth: 'calc(100vw - 300px)', // Account for sidebar width
                                  '@media (max-width: 960px)': {
                                    maxWidth: '100vw' // Full width on mobile when sidebar is collapsed
                                  }
                                }}
                              >
                                <CardContent sx={{ flexGrow: 1 }}>
                                  <TableContainer
                                    component={Card}
                                    sx={{
                                      width: '100%',
                                      maxWidth: '100%',
                                      overflow: 'auto'
                                    }}
                                  >
                                    <Table
                                      size="small"
                                      sx={{
                                        minWidth: { xs: '800px', sm: '100%' },
                                        '& .MuiTableCell-root': {
                                          fontSize: {
                                            xs: '0.75rem',
                                            sm: '0.875rem'
                                          },
                                          padding: { xs: '4px', sm: '8px' }
                                        }
                                      }}
                                    >
                                      <TableHead size="small">
                                        <StyledTableRow>
                                          <StyledTableCell
                                            sx={{ minWidth: '120px' }}
                                          >
                                            Date & Time
                                          </StyledTableCell>
                                          <StyledTableCell
                                            align="left"
                                            sx={{ minWidth: '80px' }}
                                          >
                                            Bill #
                                          </StyledTableCell>
                                          <StyledTableCell
                                            align="right"
                                            sx={{ minWidth: '80px' }}
                                          >
                                            Terminal
                                          </StyledTableCell>
                                          <StyledTableCell
                                            align="right"
                                            sx={{ minWidth: '60px' }}
                                          >
                                            Qty
                                          </StyledTableCell>
                                          <StyledTableCell
                                            align="right"
                                            sx={{ minWidth: '70px' }}
                                          >
                                            GST
                                          </StyledTableCell>
                                          <StyledTableCell
                                            align="right"
                                            sx={{ minWidth: '70px' }}
                                          >
                                            CESS
                                          </StyledTableCell>
                                          <StyledTableCell
                                            align="right"
                                            sx={{ minWidth: '80px' }}
                                          >
                                            Discount
                                          </StyledTableCell>
                                          <StyledTableCell
                                            align="right"
                                            sx={{ minWidth: '100px' }}
                                          >
                                            Payment Mode
                                          </StyledTableCell>
                                          <StyledTableCell
                                            align="right"
                                            sx={{ minWidth: '80px' }}
                                          >
                                            Total
                                          </StyledTableCell>
                                        </StyledTableRow>
                                      </TableHead>
                                      {outletSummaryData &&
                                        outletSummaryData.length > 0 && (
                                          <TableBody>
                                            <TableRow
                                              sx={{
                                                backgroundColor: '#ECF9FF'
                                              }}
                                            >
                                              <StyledTableCell
                                                align="left"
                                                sx={{ fontWeight: 'bold' }}
                                              >
                                                TOTAL:
                                              </StyledTableCell>
                                              <StyledTableCell
                                                sx={{ fontWeight: 'bold' }}
                                                align="left"
                                              >
                                                {outletSummaryData[0]
                                                  ?.Total_Bills || 0}
                                              </StyledTableCell>
                                              <StyledTableCell
                                                sx={{ fontWeight: 'bold' }}
                                                align="right"
                                              >
                                                -
                                              </StyledTableCell>
                                              <StyledTableCell
                                                sx={{ fontWeight: 'bold' }}
                                                align="right"
                                              >
                                                {outletSummaryData[0]?.Total_Qty?.toFixed(
                                                  3
                                                )}
                                              </StyledTableCell>
                                              <StyledTableCell
                                                sx={{ fontWeight: 'bold' }}
                                                align="right"
                                              >
                                                {outletSummaryData[0]?.Total_GST?.toFixed(
                                                  2
                                                )}
                                              </StyledTableCell>
                                              <StyledTableCell
                                                sx={{ fontWeight: 'bold' }}
                                                align="right"
                                              >
                                                {outletSummaryData[0]?.Total_Cess?.toFixed(
                                                  2
                                                )}
                                              </StyledTableCell>
                                              <StyledTableCell
                                                sx={{ fontWeight: 'bold' }}
                                                align="right"
                                              >
                                                {outletSummaryData[0]?.Total_Disc?.toFixed(
                                                  2
                                                )}
                                              </StyledTableCell>
                                              <StyledTableCell
                                                sx={{ fontWeight: 'bold' }}
                                                align="right"
                                              >
                                                -
                                              </StyledTableCell>
                                              <StyledTableCell
                                                sx={{ fontWeight: 'bold' }}
                                                align="right"
                                              >
                                                {outletSummaryData[0]?.Total_Sales?.toFixed(
                                                  2
                                                )}
                                              </StyledTableCell>
                                            </TableRow>
                                          </TableBody>
                                        )}
                                      <TableBody>
                                        {tableData.map((row, i) => (
                                          <StyledTableRow
                                            key={i}
                                            hover
                                            onClick={() =>
                                              handleBillRowClick(row?.Bill_Id)
                                            }
                                            sx={{ cursor: 'pointer' }}
                                          >
                                            <StyledTableCell>
                                              {moment(row?.Bill_Date).format(
                                                'DD-MM-YYYY HH:mm:ss'
                                              )}
                                            </StyledTableCell>
                                            <StyledTableCell align="left">
                                              {row?.Bill_Num}
                                            </StyledTableCell>
                                            <StyledTableCell align="right">
                                              {row?.Terminal}
                                            </StyledTableCell>
                                            <StyledTableCell align="right">
                                              {row?.Total_Qty?.toFixed(3)}
                                            </StyledTableCell>
                                            <StyledTableCell align="right">
                                              {row?.Total_GST?.toFixed(2)}
                                            </StyledTableCell>
                                            <StyledTableCell align="right">
                                              {row?.Total_Cess?.toFixed(2)}
                                            </StyledTableCell>
                                            <StyledTableCell align="right">
                                              {row?.Total_Disc?.toFixed(2)}
                                            </StyledTableCell>
                                            <StyledTableCell align="right">
                                              {row?.PaymentMode}
                                            </StyledTableCell>
                                            <StyledTableCell align="right">
                                              {row?.Total?.toFixed(2)}
                                            </StyledTableCell>
                                          </StyledTableRow>
                                        ))}
                                      </TableBody>
                                    </Table>
                                  </TableContainer>
                                </CardContent>
                                {pagecount > 1 && (
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      flexDirection: 'column',
                                      gap: 2,
                                      p: 2,
                                      backgroundColor: 'grey.50',
                                      borderTop: '1px solid',
                                      borderColor: 'divider'
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        width: '100%'
                                      }}
                                    >
                                      <Pagination
                                        count={pagecount}
                                        page={page}
                                        color="secondary"
                                        size="small"
                                        boundaryCount={2}
                                        siblingCount={0}
                                        showFirstButton
                                        showLastButton
                                        onChange={handlePageChange}
                                        sx={{
                                          '& .MuiPaginationItem-root': {
                                            transition: 'all 0.2s ease-in-out',
                                            fontSize: {
                                              xs: '0.75rem',
                                              sm: '0.875rem'
                                            }
                                          }
                                        }}
                                      />
                                    </Box>
                                    <Box
                                      sx={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        gap: 1.5,
                                        backgroundColor: 'white',
                                        p: 2,
                                        borderRadius: 1,
                                        boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                                        transition:
                                          'box-shadow 0.2s ease-in-out',
                                        '&:hover': {
                                          boxShadow:
                                            '0 2px 6px rgba(0,0,0,0.12)'
                                        }
                                      }}
                                    >
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          color: 'text.secondary',
                                          fontWeight: 500
                                        }}
                                      >
                                        Jump to
                                      </Typography>
                                      <TextField
                                        size="small"
                                        value={goToPageInput}
                                        onChange={handleGoToPageChange}
                                        onKeyPress={handleGoToPageKeyPress}
                                        placeholder="Page"
                                        sx={{
                                          width: '80px',
                                          '& .MuiOutlinedInput-root': {
                                            backgroundColor: 'white',
                                            transition: 'all 0.2s ease-in-out',
                                            '& fieldset': {
                                              borderColor: 'grey.300',
                                              borderWidth: '1px'
                                            },
                                            '&:hover fieldset': {
                                              borderColor: 'secondary.main',
                                              borderWidth: '1.5px'
                                            },
                                            '&.Mui-focused fieldset': {
                                              borderColor: 'secondary.main',
                                              borderWidth: '2px',
                                              boxShadow:
                                                '0 0 0 3px rgba(156, 39, 176, 0.1)'
                                            }
                                          },
                                          '& .MuiOutlinedInput-input': {
                                            textAlign: 'center',
                                            fontWeight: 500,
                                            padding: '7px 8px',
                                            height: '20px',
                                            lineHeight: '20px'
                                          }
                                        }}
                                      />
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          color: 'text.secondary',
                                          fontWeight: 400
                                        }}
                                      >
                                        of {pagecount}
                                      </Typography>
                                      <Button
                                        variant="contained"
                                        color="secondary"
                                        size="small"
                                        onClick={handleGoToPageSubmit}
                                        disabled={
                                          !goToPageInput ||
                                          parseInt(goToPageInput, 10) < 1 ||
                                          parseInt(goToPageInput, 10) >
                                            pagecount
                                        }
                                        sx={{
                                          textTransform: 'none',
                                          fontWeight: 500,
                                          px: 2,
                                          py: 0.75,
                                          height: '34px',
                                          lineHeight: 1,
                                          transition: 'all 0.2s ease-in-out',
                                          '&:hover': {
                                            transform: 'translateY(-1px)',
                                            boxShadow:
                                              '0 4px 8px rgba(156, 39, 176, 0.3)'
                                          },
                                          '&:active': {
                                            transform: 'translateY(0)'
                                          }
                                        }}
                                      >
                                        Go
                                      </Button>
                                    </Box>
                                  </Box>
                                )}
                              </MainCard>
                            </Grid>
                          </Grid>
                        </Grid>
                      </Grid>
                    )}
                  {!reportVisibility &&
                    !billwiseReportLoading &&
                    !billwiseReportError &&
                    !outletFetchLoading &&
                    !outletFetchError && <ShowNoRecordsFound />}
                </>
              ) : (
                <>
                  {/* <CircularProgress /> */}
                  <SkeletonCard />
                </>
              )}
            </div>
          )}
        </div>
      )}

      <div>
        <Snackbar
          open={snackbar}
          autoHideDuration={SnackBarHideDuration}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          onClose={handleSnackbarClose}
          TransitionComponent={SnackBarTransition}
        >
          <Alert onClose={handleSnackbarClose} severity={severity}>
            {PopUpMessage}
          </Alert>
        </Snackbar>
      </div>

      <LoadingDialog open={downloadReportsLoading} />

      <BillDetailsDialog
        open={billDetailsDialogOpen}
        onClose={handleBillDetailsDialogClose}
        billData={billDetailsData}
        loading={billDetailsLoading}
        error={billDetailsError}
      />
    </Box>
  )
}

export default BillwiseReport
